import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { EmailChipInput } from '@/components/ui/email-chip-input';
import { useSystemSettings } from '@/hooks/useSettings';
import type { ClientInput } from '@jobos/shared/types/client';

const clientFormSchema = z.object({
  name: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  managerName: z.string().optional(),
  contactInfo: z.string().optional(),
  // FR-1.4 override. Held as a string so an empty field means "inherit the
  // company default" rather than 0.
  overdueAfterDays: z.string().optional().refine(
    (v) => {
      if (!v || v.trim() === '') return true;
      const n = Number(v);
      return Number.isInteger(n) && n >= 1 && n <= 365;
    },
    { message: 'Enter a whole number of days between 1 and 365' },
  ),
  // FR-4.2 accounts-payable addresses. Validated here as well as by the shared
  // schema at the API boundary: one malformed address makes the mail provider
  // reject the whole multi-recipient send, so the invoice reaches nobody.
  apEmails: z.array(z.string().trim().email()).optional(),
}).refine(
  (data) => data.name?.trim() || data.firstName?.trim() || data.lastName?.trim(),
  { message: 'Either company name or individual name is required', path: ['name'] }
);

/**
 * Form values are all strings — including the numeric overdue override, which
 * has to distinguish an empty box ("inherit the company default") from 0.
 * handleFormSubmit converts it back to a number or null on the way out.
 */
type ClientFormValues = Omit<ClientInput, 'overdueAfterDays'> & {
  overdueAfterDays?: string;
};

interface ClientFormProps {
  onSubmit: (data: ClientInput) => void | Promise<void>;
  defaultValues?: Partial<ClientInput>;
  isPending?: boolean;
  submitButtonText?: string;
  showCancelButton?: boolean;
  onCancel?: () => void;
  // Edit mode sends every field (empties included) so cleared fields are
  // explicitly nulled by the backend. Create mode strips empties so optional
  // fields don't trip the backend's min(1) validation.
  isEdit?: boolean;
}

export function ClientForm({
  onSubmit,
  defaultValues,
  isPending = false,
  submitButtonText = 'Save Client',
  showCancelButton = true,
  onCancel,
  isEdit = false,
}: ClientFormProps) {
  // The placeholder shows the company default so the inherited number is
  // visible without having to save anything.
  const { data: settings } = useSystemSettings();
  const companyOverdueAfterDays = settings?.overdueAfterDays;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      name: '',
      firstName: '',
      lastName: '',
      managerName: '',
      contactInfo: '',
      apEmails: [],
      ...defaultValues,
      overdueAfterDays:
        defaultValues?.overdueAfterDays == null ? '' : String(defaultValues.overdueAfterDays),
    },
  });

  // Load default values when they change (for edit mode)
  useEffect(() => {
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        // apEmails is the one list-valued field: String() would stringify it
        // into the box instead of restoring the chips.
        if (Array.isArray(value)) {
          setValue(key as keyof ClientFormValues, value as string[]);
          return;
        }
        setValue(
          key as keyof ClientFormValues,
          value == null ? '' : String(value),
        );
      });
    }
  }, [defaultValues, setValue]);

  const handleFormSubmit = (data: ClientFormValues) => {
    const cleanedData: Record<string, string | number | null | string[]> = {};
    for (const [key, value] of Object.entries(data)) {
      // The overdue override is the one numeric field on this form: an empty
      // box means "inherit the company default", which the backend stores as
      // NULL — not as the empty string every other field trims to.
      if (key === 'overdueAfterDays') {
        const raw = String(value ?? '').trim();
        if (raw) cleanedData[key] = Number(raw);
        else if (isEdit) cleanedData[key] = null;
        continue;
      }
      // The list-valued field. Edit sends [] so a cleared list is actually
      // cleared; create omits an empty list rather than sending [].
      if (key === 'apEmails') {
        const emails = (value as string[] | undefined) ?? [];
        if (isEdit || emails.length > 0) cleanedData[key] = emails;
        continue;
      }
      const trimmed = (value as string)?.trim() ?? '';
      // Edit: keep every field, even when empty, so a cleared field is sent and
      // the backend nulls it. Create: strip empties so optional fields don't
      // fail the backend's min(1) validation.
      if (isEdit || trimmed) {
        cleanedData[key] = trimmed;
      }
    }
    onSubmit(cleanedData as unknown as ClientInput);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <Input {...register('name')} placeholder="Enter company name" />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
            )}
          </div>

          <p className="text-xs text-gray-500 -mt-2">
            Or enter individual name below
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <Input {...register('firstName')} placeholder="First name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <Input {...register('lastName')} placeholder="Last name" />
            </div>
          </div>
        </div>

        {/* Billing */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Flag invoices overdue after
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={365}
                className="w-28"
                placeholder={companyOverdueAfterDays ? String(companyOverdueAfterDays) : '30'}
                {...register('overdueAfterDays')}
              />
              <span className="text-sm text-gray-500">days</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to use the company default
              {companyOverdueAfterDays ? ` (${companyOverdueAfterDays} days)` : ''}.
            </p>
            {errors.overdueAfterDays && (
              <p className="text-sm text-red-600 mt-1">{errors.overdueAfterDays.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="apEmails" className="block text-sm font-medium text-gray-700 mb-1">
              Accounts payable emails
            </label>
            <Controller
              name="apEmails"
              control={control}
              render={({ field }) => (
                <EmailChipInput
                  id="apEmails"
                  value={field.value ?? []}
                  onChange={field.onChange}
                  aria-describedby="apEmails-help"
                />
              )}
            />
            <p id="apEmails-help" className="text-xs text-gray-500 mt-1">
              Invoices for this client are emailed to these addresses, along with the
              manager on the job.
            </p>
            {errors.apEmails && (
              <p className="text-sm text-red-600 mt-1">{errors.apEmails.message}</p>
            )}
          </div>
        </div>

      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4">
        {showCancelButton && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Saving...' : submitButtonText}
        </Button>
      </div>
    </form>
  );
}
